import React, { createContext, useContext, useMemo, useSyncExternalStore } from "react";
import { appHistory } from "@/router/history";

import SessionApi from "api/SessionApi";
import i18n, { normalizeLanguage } from "config/i18n";
import { queryClient } from "api/queries/queryClient";
import { prepareAdminBorrowSession } from "./adminBorrowSession";
import {
  getApiData,
  getApiErrorCode,
  type ApiError,
  type UpdateProfilePayload,
} from "../types/api";
import type { FlashMessage, SessionState, UserCustomer, UserData } from "../types/state";

const SESSION_TIMEOUT_MESSAGE: FlashMessage = {
  type: "error",
  content: "Vous avez été déconnecté suite à une trop longue période d'inactivité.",
};

interface LoginCredentialsInput {
  username: string;
  password: string;
}

interface UpdateParametersInput {
  allow_email?: boolean | number;
  allow_notification?: boolean | number;
  display_notification?: boolean | number;
  language?: string;
  password?: string;
  passwordConfirm?: string;
}

export interface AuthActions {
  logInUser: (credentials: LoginCredentialsInput) => Promise<void>;
  logOutUser: () => void;
  logOutAdmin: () => void;
  removeLoginMessage: () => void;
  unsetCustomer: () => void;
  changeCustomer: (customer: UserCustomer) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getEmailInformation: (code: string) => Promise<void>;
  removeResetPasswordMessage: () => void;
  removeEmailInformationMessage: () => void;
  checkResetToken: (token: string) => Promise<void>;
  setNewPassword: (email: string, password: string, token: string) => Promise<void>;
  updateProfile: (data: UpdateProfilePayload) => Promise<void>;
  updateParameters: (data: UpdateParametersInput) => Promise<void>;
  acceptLicence: () => Promise<void>;
  refuseLicence: () => Promise<void>;
  decrementUnreadNotificationCount: (customerId: number | string, allClear?: boolean) => void;
  setUnreadNotificationCount: (customerId: number | string, count: number | string) => boolean;
  borrowCustomer: (code: string) => Promise<void>;
  checkAdminToken: (token?: string | null) => Promise<void>;
  saveAdminToken: (token: string) => void;
  setRedirect: (route: string | null) => void;
  unloadRedirect: () => void;
}

export interface AuthContextValue extends SessionState {
  route: string | null;
  actions: AuthActions;
  authActions: Pick<AuthActions, "setRedirect" | "unloadRedirect">;
  sessionActions: AuthActions;
}

export type AuthInjectedProps = AuthContextValue & {
  actions: AuthActions;
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function readUserData(): UserData {
  return readJson<UserData>("userData", {});
}

function readCustomerData(): UserCustomer | Record<string, never> {
  return readJson<UserCustomer | Record<string, never>>("customerData", {});
}

interface AuthStoreState extends SessionState {
  route: string | null;
}

const initialState: AuthStoreState = {
  adminToken: null,
  userLoggedIn: !!localStorage.getItem("userData"),
  userData: readUserData(),
  customerLoggedIn: !!localStorage.getItem("customerData"),
  customerData: readCustomerData(),
  loginPageMessage: null,
  chooseCustomerPageMessage: null,
  resetPasswordPageMessage: null,
  newPasswordPageMessage: null,
  borrowCustomerPageMessage: null,
  profilePageMessage: null,
  isResetTokenValid: false,
  route: null,
};

let state = initialState;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function setState(
  updater: Partial<AuthStoreState> | ((current: AuthStoreState) => AuthStoreState)
) {
  state = typeof updater === "function" ? updater(state) : { ...state, ...updater };
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

function clearUserSession() {
  localStorage.removeItem("userData");
  localStorage.removeItem("customerData");
  queryClient.clear();
}

function completeCustomerSession(userData: UserData, customer: UserCustomer) {
  const redirectRoute = state.route;

  localStorage.setItem("customerData", JSON.stringify(customer));
  localStorage.setItem("userData", JSON.stringify(userData));
  queryClient.removeQueries({ queryKey: ["banner"] });

  setState((current) => ({
    ...current,
    userLoggedIn: true,
    customerLoggedIn: true,
    customerData: customer,
    userData: { ...current.userData, ...userData },
    chooseCustomerPageMessage: null,
    route: null,
  }));

  appHistory.push(redirectRoute || BASE_PATH + "/");
}

export function expireSession() {
  setState({ loginPageMessage: SESSION_TIMEOUT_MESSAGE });
  authActions.logOutUser();
}

export const authActions: AuthActions = {
  logInUser(credentials) {
    return SessionApi.login(credentials).then(
      (res) => {
        const content = getApiData(res, {}) as {
          isAdmin?: boolean;
          admin_token?: string;
          userData?: UserData;
        };

        if (content.isAdmin || content.admin_token) {
          setState({ adminToken: content.admin_token || true, loginPageMessage: null });
          appHistory.push(BASE_PATH + "/borrow_customer");
          return undefined;
        }

        const userData = content.userData || {};
        localStorage.setItem("userData", JSON.stringify(userData));
        setState((current) => ({
          ...current,
          userLoggedIn: true,
          userData: { ...current.userData, ...userData },
          loginPageMessage: null,
          borrowCustomerPageMessage: null,
        }));

        if (userData.customer) {
          if (userData.has_accepted_eula) {
            completeCustomerSession(userData, userData.customer);
          } else {
            appHistory.push(BASE_PATH + "/end_user_agreement");
          }
        } else {
          appHistory.push(BASE_PATH + "/choose_customer");
        }

        return undefined;
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "ACCOUNT_BLOCKED") {
          appHistory.push(BASE_PATH + "/blocked_account");
        } else if (getApiErrorCode(error) === "LOGIN_ERROR") {
          setState({
            loginPageMessage: {
              type: "error",
              content: i18n.t("login.error_invalid_credentials"),
            },
          });
        } else if (getApiErrorCode(error) === "INACTIVE_ACCOUNT") {
          setState({
            loginPageMessage: {
              type: "error",
              content: i18n.t("login.error_inactive_account"),
            },
          });
        }
      }
    );
  },

  logOutUser() {
    SessionApi.logout().catch(() => undefined);
    const wasAdmin = Boolean(localStorage.getItem("adminBorrow"));
    localStorage.removeItem("adminBorrow");
    clearUserSession();
    setState((current) => ({
      ...current,
      userLoggedIn: false,
      userData: {},
      customerLoggedIn: false,
      customerData: {},
    }));

    appHistory.push(wasAdmin ? BASE_PATH + "/borrow_customer" : BASE_PATH + "/login");
  },

  logOutAdmin() {
    SessionApi.logout().catch(() => undefined);
    localStorage.removeItem("adminBorrow");
    setState({ adminToken: null });
    appHistory.push(BASE_PATH + "/login");
  },

  removeLoginMessage() {
    setState({ loginPageMessage: null });
  },

  unsetCustomer() {
    localStorage.removeItem("customerData");
    queryClient.removeQueries({ queryKey: ["banner"] });
    setState({ customerLoggedIn: false, customerData: {} });
    appHistory.push(BASE_PATH + "/choose_customer");
  },

  changeCustomer(customer) {
    return SessionApi.switchCustomer(customer.id).then(
      (res) => {
        const content = getApiData(res, {}) as { userData?: UserData };
        const userData = content.userData || {};
        if (userData.has_accepted_eula) {
          completeCustomerSession(userData, customer);
          return;
        }

        localStorage.setItem("userData", JSON.stringify(userData));
        setState((current) => ({
          ...current,
          userLoggedIn: true,
          customerLoggedIn: false,
          customerData: {},
          userData: { ...current.userData, ...userData },
          chooseCustomerPageMessage: null,
        }));
        appHistory.push(BASE_PATH + "/end_user_agreement");
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          setState({ loginPageMessage: SESSION_TIMEOUT_MESSAGE });
          authActions.logOutUser();
          return;
        }

        localStorage.removeItem("customerData");
        setState({
          userLoggedIn: true,
          customerLoggedIn: false,
          customerData: {},
          chooseCustomerPageMessage: {
            type: "error",
            content: i18n.t("customer.choose.error_inactive_customer"),
          },
        });
        appHistory.push(BASE_PATH + "/choose_customer");
      }
    );
  },

  resetPassword(email) {
    return SessionApi.resetPassword(email).then(
      () => {
        setState({
          loginPageMessage: {
            type: "success",
            content: "Un mail vient de vous être envoyé afin de réinitialiser votre mot de passe.",
          },
          resetPasswordPageMessage: null,
        });
        appHistory.push(BASE_PATH + "/login");
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "UNKNOWN_USER") {
          setState({
            resetPasswordPageMessage: {
              type: "error",
              content: "Veuillez vérifier votre adresse email.",
            },
          });
        }
      }
    );
  },

  getEmailInformation(code) {
    return SessionApi.emailInformation(code).then(
      (res) => {
        const email = (res as { email?: string }).email;
        setState({
          resetPasswordPageEmailMessage: {
            type: "success",
            content: "Voici l'email associé à votre compte : " + email,
          },
        });
      },
      () => {
        setState({
          resetPasswordPageEmailMessage: {
            type: "error",
            content: "Veuillez vérifier votre code facturé.",
          },
        });
      }
    );
  },

  removeResetPasswordMessage() {
    setState({ resetPasswordPageMessage: null });
  },

  removeEmailInformationMessage() {
    setState({ resetPasswordPageEmailMessage: null });
  },

  checkResetToken(token) {
    return SessionApi.checkResetToken(token).then(
      () => setState({ newPasswordPageMessage: null, isResetTokenValid: true }),
      (error: ApiError) => {
        let message: FlashMessage | null = null;
        if (getApiErrorCode(error) === "UNKNOWN_RESET_TOKEN") {
          message = {
            type: "error",
            content: "Le lien de réinitialisation de mot de passe n'est pas valide.",
          };
        } else if (getApiErrorCode(error) === "EXPIRED_RESET_TOKEN") {
          message = {
            type: "error",
            content: "Le lien de réinitialisation de mot de passe a expiré.",
          };
        }

        setState({ newPasswordPageMessage: message, isResetTokenValid: false });
      }
    );
  },

  setNewPassword(email, password, token) {
    return SessionApi.setNewPassword(email, password, token).then(
      () => {
        setState({
          loginPageMessage: {
            type: "success",
            content: "Votre mot de passe vient d'être mis à jour.",
          },
          newPasswordPageMessage: null,
        });
        appHistory.push(BASE_PATH + "/login");
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "BAD_PASSWORD") {
          setState({
            newPasswordPageMessage: {
              type: "error",
              content:
                "Le mot de passe doit être composé d'au moins 8 caractères, d'une minuscule, d'une majuscule et d'un chiffre.",
            },
          });
        }
      }
    );
  },

  updateProfile(data) {
    return SessionApi.updateProfile(data).then(
      (res) => {
        const content = getApiData(res, {}) as { userData?: Partial<UserData> };
        const updatedUserData = content.userData || {};
        const persistedUserData = { ...readUserData(), ...updatedUserData };

        localStorage.setItem("userData", JSON.stringify(persistedUserData));
        setState((current) => ({
          ...current,
          userData: { ...current.userData, ...updatedUserData },
          profilePageMessage: {
            type: "success",
            content: "Vos informations viennent d'être mises à jour.",
          },
        }));
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          expireSession();
        }
        if (getApiErrorCode(error) === "BAD_PASSWORD") {
          setState({
            profilePageMessage: {
              type: "error",
              content:
                "Le mot de passe doit être composé d'au moins 8 caractères, d'une minuscule, d'une majuscule et d'un chiffre.",
            },
          });
        }
      }
    );
  },

  updateParameters(data) {
    return SessionApi.updateParameters(data).then(
      () => {
        const userData = readUserData();
        const persistedLanguage =
          normalizeLanguage(data.language) || normalizeLanguage(userData.language) || "fr";
        userData.allow_notification = data.allow_notification;
        userData.display_notification = data.display_notification;
        userData.allow_email = data.allow_email;
        userData.language = persistedLanguage;
        userData.must_change_password = 0;
        localStorage.setItem("userData", JSON.stringify(userData));
        void i18n.changeLanguage(persistedLanguage);

        setState((current) => ({
          ...current,
          userData: { ...current.userData, ...userData },
          profilePageMessage: {
            type: "success",
            content: "Vos informations viennent d'être mises à jour.",
          },
        }));
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          expireSession();
        }
        if (getApiErrorCode(error) === "BAD_REQUEST") {
          setState({ profilePageMessage: { type: "error", content: "Mauvaise requête." } });
        }
      }
    );
  },

  acceptLicence() {
    return SessionApi.changeLicenceAcceptance(1).then(
      () => {
        const userData = readUserData();
        userData.has_accepted_eula = true;
        localStorage.setItem("userData", JSON.stringify(userData));
        setState((current) => ({ ...current, userData: { ...current.userData, ...userData } }));

        if (userData.customer) {
          completeCustomerSession(userData, userData.customer);
          return;
        }
        appHistory.push(BASE_PATH + "/choose_customer");
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          setState({ loginPageMessage: SESSION_TIMEOUT_MESSAGE });
        }
        authActions.logOutUser();
      }
    );
  },

  refuseLicence() {
    return SessionApi.changeLicenceAcceptance(0).then(
      () => {
        const userData = readUserData();
        userData.has_accepted_eula = false;
        localStorage.setItem("userData", JSON.stringify(userData));
        setState((current) => ({ ...current, userData: { ...current.userData, ...userData } }));
        authActions.logOutUser();
      },
      (error: ApiError) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          setState({ loginPageMessage: SESSION_TIMEOUT_MESSAGE });
        }
        authActions.logOutUser();
      }
    );
  },

  decrementUnreadNotificationCount(customerId, allClear = false) {
    let userData = readUserData();
    let customerData = readCustomerData() as UserCustomer;
    let customerIndex: number | null = null;
    const customers = (userData.customers || []) as UserCustomer[];
    const customer = customers.find((c, index) => {
      if (c.id == customerId) {
        customerIndex = index;
        return true;
      }
      return false;
    });

    if (customerIndex !== null && customer) {
      const updated: UserCustomer = { ...customer };
      updated.unread_notification_count = allClear
        ? 0
        : (updated.unread_notification_count || 0) - 1;

      customers[customerIndex] = updated;
      userData = { ...userData, customers };
      customerData = { ...customerData, ...updated };
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("customerData", JSON.stringify(customerData));
      setState((current) => ({
        ...current,
        userData: { ...current.userData, ...userData },
        customerData,
      }));
    }
  },

  setUnreadNotificationCount(customerId, count) {
    let userData = readUserData();
    let customerData = readCustomerData() as UserCustomer | Record<string, never>;
    const parsedCount = parseInt(String(count || 0), 10);
    const unreadCount = isNaN(parsedCount) ? 0 : Math.max(0, parsedCount);
    let hasChanged = false;

    if (
      customerData &&
      (customerData as UserCustomer).id != undefined &&
      (customerData as UserCustomer).id == customerId
    ) {
      const cd = customerData as UserCustomer;
      const currentCustomerUnread = parseInt(String(cd.unread_notification_count || 0), 10);
      if (currentCustomerUnread !== unreadCount) {
        hasChanged = true;
        customerData = { ...cd, unread_notification_count: unreadCount };
        localStorage.setItem("customerData", JSON.stringify(customerData));
      }
    }

    if (Array.isArray(userData.customers)) {
      let hasUpdatedCustomer = false;
      const customers = userData.customers.map((c: UserCustomer) => {
        if (c.id == customerId) {
          const currentUnread = parseInt(String(c.unread_notification_count || 0), 10);
          if (currentUnread !== unreadCount) {
            hasUpdatedCustomer = true;
            return { ...c, unread_notification_count: unreadCount };
          }
        }
        return c;
      });

      if (hasUpdatedCustomer) {
        hasChanged = true;
        userData = { ...userData, customers };
        localStorage.setItem("userData", JSON.stringify(userData));
      }
    }

    if (hasChanged) {
      setState((current) => ({
        ...current,
        userData: { ...current.userData, ...userData },
        customerData,
      }));
    }

    return hasChanged;
  },

  borrowCustomer(code) {
    const adminToken = typeof state.adminToken === "string" ? state.adminToken : null;

    return SessionApi.borrowCustomer(code, adminToken).then(
      (res) => {
        const content = getApiData(res, {}) as { userData?: UserData };
        const userData = content.userData || {};
        localStorage.setItem("userData", JSON.stringify(userData));
        localStorage.setItem("adminBorrow", "1");
        setState((current) => ({
          ...current,
          userLoggedIn: userData.customer ? current.userLoggedIn : true,
          userData: { ...current.userData, ...userData },
          loginPageMessage: null,
          borrowCustomerPageMessage: null,
        }));

        if (userData.customer) {
          completeCustomerSession(userData, userData.customer);
        }

        return undefined;
      },
      (error: ApiError) => {
        switch (getApiErrorCode(error)) {
          case "UNKNOWN_ADMIN_TOKEN":
          case "EXPIRED_ADMIN_TOKEN":
            setState({ loginPageMessage: SESSION_TIMEOUT_MESSAGE });
            authActions.logOutAdmin();
            break;
          case "CUSTOMER_NOT_FOUND":
          case "USER_NOT_FOUND":
            setState({
              borrowCustomerPageMessage: {
                type: "error",
                content: "Le numéro client ne correpond pas à un compte connu ou actif.",
              },
            });
        }
      }
    );
  },

  checkAdminToken(token = null) {
    return SessionApi.checkAdminToken(token)
      .then(() => undefined)
      .catch((error: ApiError) => {
        switch (getApiErrorCode(error)) {
          case "UNKNOWN_ADMIN_TOKEN":
          case "EXPIRED_ADMIN_TOKEN":
            setState({ loginPageMessage: SESSION_TIMEOUT_MESSAGE });
            authActions.logOutAdmin();
            break;
          default:
            setState({
              borrowCustomerPageMessage: {
                type: "error",
                content:
                  "La connexion au compte client n'a pas pu être initialisée. Veuillez réessayer.",
              },
            });
        }
      });
  },

  saveAdminToken(token) {
    if (state.adminToken === token && !state.userLoggedIn && !state.customerLoggedIn) {
      return;
    }

    prepareAdminBorrowSession();
    setState((current) => ({
      ...current,
      adminToken: token,
      userLoggedIn: false,
      userData: {},
      customerLoggedIn: false,
      customerData: {},
      loginPageMessage: null,
      borrowCustomerPageMessage: null,
    }));
  },

  setRedirect(route) {
    if (state.route === route) {
      return;
    }

    setState({ route });
  },

  unloadRedirect() {
    if (state.route === null) {
      return;
    }

    setState({ route: null });
  },
};

const redirectAuthActions: Pick<AuthActions, "setRedirect" | "unloadRedirect"> = {
  setRedirect: authActions.setRedirect,
  unloadRedirect: authActions.unloadRedirect,
};

const AuthContext = createContext<AuthContextValue | null>(null);

function createContextValue(snapshot: AuthStoreState): AuthContextValue {
  return {
    ...snapshot,
    actions: authActions,
    sessionActions: authActions,
    authActions: redirectAuthActions,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const value = useMemo(() => createContextValue(snapshot), [snapshot]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return value;
}

export function withAuthData<P extends Partial<AuthInjectedProps>>(
  Component: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof AuthInjectedProps>> {
  return function AuthData(props) {
    const auth = useAuth();

    return (
      <Component
        {...(props as P)}
        {...auth}
        actions={auth.actions}
        sessionActions={auth.sessionActions}
        authActions={auth.authActions}
      />
    );
  };
}
